import { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

const BUCKET_NAME = "website-images";

function sanitizeFileName(fileName) {
    const extension = fileName.split(".").pop()?.toLowerCase() || "jpg";
    const baseName = fileName
        .replace(/\.[^/.]+$/, "")
        .toLowerCase()
        .trim()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return `${baseName || "bild"}.${extension}`;
}

export default function ImageUploadField({
                                             label = "Bild",
                                             value,
                                             onChange,
                                             folder = "uploads",
                                             primaryColor = "#8D6E63",
                                             secondaryColor = "#A1887F",
                                             previewAlt = "Bildvorschau",
                                         }) {
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    async function handleFileChange(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        setErrorMessage("");

        if (!file.type.startsWith("image/")) {
            setErrorMessage("Bitte wähle eine Bilddatei aus.");
            return;
        }

        const maxSizeInMb = 8;
        const maxSizeInBytes = maxSizeInMb * 1024 * 1024;

        if (file.size > maxSizeInBytes) {
            setErrorMessage(`Das Bild darf maximal ${maxSizeInMb} MB groß sein.`);
            return;
        }

        setIsUploading(true);

        const safeFileName = sanitizeFileName(file.name);
        const filePath = `${folder}/${Date.now()}-${safeFileName}`;

        const { error: uploadError } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(filePath, file, {
                cacheControl: "3600",
                upsert: false,
                contentType: file.type,
            });

        if (uploadError) {
            console.error("Fehler beim Upload:", uploadError);
            setErrorMessage("Das Bild konnte nicht hochgeladen werden.");
            setIsUploading(false);
            return;
        }

        const { data } = supabase.storage
            .from(BUCKET_NAME)
            .getPublicUrl(filePath);

        onChange(data.publicUrl);
        setIsUploading(false);
    }

    return (
        <div style={adminStyles.field}>
            <label style={adminStyles.label}>{label}</label>

            {value && (
                <div
                    style={{
                        width: "100%",
                        maxWidth: "420px",
                        aspectRatio: "16 / 10",
                        borderRadius: "20px",
                        overflow: "hidden",
                        backgroundColor: "#f1ebe6",
                        marginBottom: "1rem",
                        border: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <img
                        src={value}
                        alt={previewAlt}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                </div>
            )}

            <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isUploading}
                style={{
                    ...adminStyles.input,
                    cursor: isUploading ? "not-allowed" : "pointer",
                    backgroundColor: "#fff",
                }}
            />

            <input
                type="text"
                value={value || ""}
                onChange={(event) => onChange(event.target.value)}
                style={{
                    ...adminStyles.input,
                    marginTop: "0.75rem",
                }}
                placeholder="Oder Bild-URL manuell einfügen"
            />

            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    style={{
                        ...adminStyles.secondaryButton,
                        backgroundColor: secondaryColor,
                        marginTop: "0.75rem",
                    }}
                >
                    Bild entfernen
                </button>
            )}

            {isUploading && (
                <p style={{ color: "#666", marginTop: "0.75rem" }}>
                    Bild wird hochgeladen...
                </p>
            )}

            {errorMessage && (
                <p
                    style={{
                        color: "#a40000",
                        backgroundColor: "#fff0f0",
                        padding: "0.75rem 1rem",
                        borderRadius: "12px",
                        marginTop: "0.75rem",
                    }}
                >
                    {errorMessage}
                </p>
            )}
        </div>
    );
}